import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { HandCoins, ChevronDown, ChevronUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PayStateBadge, PaymentDialog, useCompanies } from "@/components/SalesShared";

function CustomerRow({ g, idx, companies, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t hover:bg-secondary/50 cursor-pointer" onClick={() => setOpen((o) => !o)} data-testid={`receivable-row-${idx}`}>
        <td className="px-4 py-2.5 font-medium">
          <span className="inline-flex items-center gap-1">{open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}{g.customer_name}</span>
          <span className="block text-xs text-muted-foreground pl-4">{g.customer_phone || "-"} · {g.transactions.length} nota</span>
        </td>
        <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(g.total)}</td>
        <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{formatRupiah(g.paid)}</td>
        <td className="px-4 py-2.5 text-right font-mono font-semibold text-rose-600" data-testid={`receivable-remaining-${idx}`}>{formatRupiah(g.remaining)}</td>
        <td className="px-4 py-2.5"><PayStateBadge state={g.status} testId={`receivable-status-${idx}`} /></td>
      </tr>
      {open && g.transactions.map((t) => (
        <tr key={t.id} className="bg-secondary/40 text-xs" data-testid={`receivable-tx-${t.id}`}>
          <td className="px-4 py-2 pl-9"><Link to={`/transactions/${t.id}`} className="text-brand hover:underline font-mono">{t.nota_number}</Link> · {formatDate(t.date)} · JT {formatDate(t.due_date)}</td>
          <td className="px-4 py-2 text-right font-mono">{formatRupiah(t.amount)}</td>
          <td className="px-4 py-2 text-right font-mono">{formatRupiah(t.paid_total)}</td>
          <td className="px-4 py-2 text-right font-mono">{formatRupiah(t.remaining)}</td>
          <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
            {t.remaining > 0 ? <PaymentDialog tx={t} companies={companies} onDone={onDone} testId={`receivable-pay-${t.id}`} /> : <PayStateBadge state="lunas" />}
          </td>
        </tr>
      ))}
    </>
  );
}

export default function Receivables() {
  const companies = useCompanies();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = useCallback(() => { api.get("/receivables").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const s = q.trim().toLowerCase();
  const filtered = rows.filter((g) => !s || g.customer_name.toLowerCase().includes(s) || (g.customer_phone || "").includes(s));
  const sum = (k) => filtered.reduce((t, g) => t + g[k], 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><HandCoins className="h-6 w-6 text-brand" /> Piutang</h2>
          <p className="text-sm text-muted-foreground">Transaksi kredit per pelanggan. Klik baris untuk melihat nota & catat pembayaran.</p>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pelanggan / HP" className="max-w-xs h-9" data-testid="receivable-search" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4"><p className="text-xs text-muted-foreground">Total Tagihan</p><p className="text-xl font-bold font-mono" data-testid="receivable-sum-total">{formatRupiah(sum("total"))}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Sudah Dibayar</p><p className="text-xl font-bold font-mono text-emerald-700" data-testid="receivable-sum-paid">{formatRupiah(sum("paid"))}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Sisa Piutang</p><p className="text-xl font-bold font-mono text-rose-600" data-testid="receivable-sum-remaining">{formatRupiah(sum("remaining"))}</p></Card>
      </div>
      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6"><div className="h-4 rounded-md skeleton-shimmer" /></div> : filtered.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground" data-testid="receivable-empty">Belum ada transaksi kredit.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="receivable-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr><th className="text-left px-4 py-2.5">Nama Pelanggan</th><th className="text-right px-4 py-2.5">Total Tagihan</th><th className="text-right px-4 py-2.5">Sudah Dibayar</th>
                  <th className="text-right px-4 py-2.5">Sisa Piutang</th><th className="text-left px-4 py-2.5">Status</th></tr>
              </thead>
              <tbody>{filtered.map((g, i) => <CustomerRow key={g.key} g={g} idx={i} companies={companies} onDone={load} />)}</tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
